import { CollateralSecurityTypes, FacilityTypes } from './DealDebtSchedule';
import {
	BaseRateTypes,
	CovenantTargetTypes,
	FacilityAvailabilityLimitTypes,
	InterestRateTypes,
	RateTypes,
	TermSheetFeeTypes,
} from './TermSheet';
import { StructureTranches } from './TrancheEvaluation';

export interface TermSheetRevision {
	id: number;
	guid: string;
	termSheetGuid: string;

	termSheetFileName: string;
	termSheetFileType: string;
	isCurrentVersion: boolean;
	versionNumber: number;
	isAccepted: boolean;
	borrowerNotes: string;
	submittedOn?: Date;
	modifiedOn?: Date;
	decisionOn?: Date;
	submittedByName: string;
	fileSize: number;
	visibleToBorrower: boolean;

	termSheetType?: TermSheetTypes;
	termSheetGovernanceType?: TermSheetGovernanceTypes;
	numberBoardSeats?: number;
	hasPersonalGuaranty: boolean;
	personalGuaranteeType?: PersonalGuaranteeTypes;
	personalGuarantyDetails: string;
	otherTermsNotes: string;

	warrantRequired: boolean;
	warrantAmount?: number;
	isWarrantAmountTbd?: boolean;
	warrantCoverageType?: WarrantCoverageTypes;
	warrantPrice?: number;
	isWarrantPriceTbd?: boolean;

	dateReceived?: Date;
	responseStatus: TermSheetResponses;
	costOfCapital?: number;
	termsEffectiveDate?: string;
	covenantsProvided: boolean;
	reportingProvided: boolean;
	governanceDetails: string;
	warrantDetails: string;

	facilities: TermSheetFacility[];
	covenants: TermSheetFinancialCovenant[];
	reporting: TermSheetFinancialReporting[];
	conditions: TermSheetCondition[];
	incompleteData: string[];
}

export type TermSheetTypes = keyof typeof TermSheetTypesMap;
export const TermSheetTypesMap = {
	TermSheet: 'Term Sheet',
	IOI: 'Indication of Interest (IOI)',
	ExistingDebtTerms: 'Existing Debt Terms',
};

export type PersonalGuaranteeTypes = keyof typeof PersonalGuaranteeTypesMap;
export const PersonalGuaranteeTypesMap = {
	Personal: 'Personal',
	Corporate: 'Corporate',
	Both: 'Personal & Corporate',
	ValiditySupport: 'Validity & Support',
	ValiditySupportAndCorporate: 'Validity & Support + Corporate',
};

export type TermSheetGovernanceTypes = keyof typeof TermSheetGovernanceTypesMap;
export const TermSheetGovernanceTypesMap = {
	NoValue: '',
	None: 'None',
	ObserverRights: 'Observer Rights',
	BoardSeats: 'Board Seat(s)',
};

export type WarrantCoverageTypes = keyof typeof WarrantCoverageTypesMap;
export const WarrantCoverageTypesMap = {
	None: 'None',
	Full: 'Full',
	Loan: 'Loan',
};

export type TermSheetResponses = keyof typeof TermSheetResponsesMap;
export const TermSheetResponsesMap = {
	Replaced: 'Replaced',
	Pending: 'Pending',
	Accepted: 'Accepted',
	Rejected: 'Rejected',
};

export interface TermSheetFacility {
	id: number;
	guid: string;
	itemIdentifier: string;
	highEndAmount: number;
	facilityType: FacilityTypes;
	highEndInterestRate: number;
	rateType: RateTypes;
	interestRateType: InterestRateTypes;
	baseRate: BaseRateTypes;
	baseRateValue: number;
	termMonths: number;
	highEndMaturity: number;
	isAccordion: boolean;
	accordionAmount?: number;
	ioPeriod?: number;
	amortizationStructure?: FacilityAmortizationStructures;
	facilityAmortizationPeriod?: FacilityAmortizationPeriods;
	ecfSweep?: string;
	interestCash?: number;
	baseRateFloor?: number;
	pikInterest?: number;
	pikInterestCompounded: boolean;
	pikInterestPeriod?: FacilityAmortizationPeriods;
	structureTranche: StructureTranches;
	leaseRateFactor?: number;
	leaseResidual?: number;
	exerciseLeaseResidual: boolean;
	requiresDeposit: boolean;
	depositRefundType?: DepositRefundTypes;
	depositAmount?: number;
	facilityFactorType: FacilityFactorTypes;
	leasePaymentAmount?: number;
	isInterestOnlyPeriod?: boolean;
	interestOnlyPeriodDuration?: InterestOnlyPeriodDurations;
	otherBaseRateName?: string;
	otherBaseRateValue?: number;
	feesProvided: boolean;
	pricingComments: string;
	hasGracePeriod: boolean;
	gracePeriodLength?: number;
	gracePeriodLengthType?: InterestOnlyPeriodDurations;
	amortizationPeriodLength?: number;
	isDemandLine: boolean;
	maturityDate?: string;

	painPoint: TermSheetFacilityPainPoint | null;
	fees: TermSheetFacilityFee[];
	collateral: TermSheetFacilityCollateral[];
	incompleteData: string[];
}

export interface TermSheetFacilityPainPoint {
	id: number;
	guid: string;
	maturityDate: string;
	personalGuarantee: boolean;
	personalGuaranteeText: string;
	interestRate: boolean;
	interestRateText: string;
	covenants: boolean;
	covenantsText: string;
	maturity: boolean;
	maturityText: string;
	amortization: boolean;
	amortizationText: string;
	loanAmount: boolean;
	loanAmountText: string;
	prepaymentFees: boolean;
	prepaymentFeesText: string;
	advanceRateAgainstCollateral: boolean;
	advanceRateAgainstCollateralText: string;
	collateralRequirements: boolean;
	collateralRequirementsText: string;
	otherDetails: string;
	ttmRevenue?: number;
	ttmEbitda?: number;
	mrr?: number;
	existingDebt?: number;
	balanceSheetAr?: number;
	balanceSheetCash?: number;
	balanceSheetInventory?: number;
	balanceSheetEquipment?: number;
	balanceSheetRealEstate?: number;
	balanceSheetFurnitureFixtures?: number;
	balanceSheetVehicles?: number;
	balanceSheetMarineVessels?: number;
	balanceSheetHelicopters?: number;
	balanceSheetFixedWingAircrafts?: number;
	balanceSheetRoyalty?: number;
	balanceSheetIp?: number;
	financialsAsOfDate: string;
	createdOn: string;
	isActive: boolean;
}

export type InterestOnlyPeriodDurations = keyof typeof InterestOnlyPeriodDurationsMap;
export const InterestOnlyPeriodDurationsMap = {
	Months: 'Months',
	Years: 'Years',
};

export type FacilityFactorTypes = keyof typeof FacilityFactorTypesMap;
export const FacilityFactorTypesMap = {
	NotApplicable: 'N/A',
	RateFactor: 'Rate Factor',
	PaymentAmount: 'Payment Amount',
};

export type FacilityAmortizationStructures = keyof typeof FacilityAmortizationStructuresMap;
export const FacilityAmortizationStructuresMap = {
	None: 'None',
	StraightLine: 'Straight-Line',
	Mortgage: 'Mortgage',
	Custom: 'Custom',
};

export type DepositRefundTypes = keyof typeof DepositRefundTypesMap;
export const DepositRefundTypesMap = {
	None: 'N/A',
	YesUnusedPortion: 'Yes, Unused Portion',
	Yes: 'Yes',
	NotRefundable: 'Not Refundable',
};

export interface TermSheetFacilityFee {
	id: number;
	guid: string;
	termSheetFeeGuid: string;
	termSheetFee: TermSheetFee;
	feeAmount: number;
	termSheetFeeType: TermSheetFeeTypes;
	feeFrequency: TermSheetFeeFrequencies;
}

export interface ConditionType {
	id: number;
	guid: string;
	conditionTypeName: string;
}

export interface TermSheetFee {
	id: number;
	guid: string;
	feeName: string;
	isActive: boolean;
	isTermLoan: boolean;
	isRevolver: boolean;
	isLease: boolean;
	excludedFromCostOfCapital: boolean;
	isPrepaymentFee: boolean;
	termSheetFeeType: TermSheetFeeTypes;
	feeFrequency: TermSheetFeeFrequencies;
}

export type TermSheetFeeFrequencies = keyof typeof TermSheetFeeFrequenciesMap;
export const TermSheetFeeFrequenciesMap = {
	OneTimeUpfront: 'One-Time Upfront',
	OneTimeBackend: 'One-Time Backend',
	Monthly: 'Monthly',
	Quarterly: 'Quarterly',
	Annually: 'Annually',
};

export interface TermSheetFinancialCovenant {
	id: number;
	guid: string;
	financialCovenantTypeGuid: string;
	financialCovenantType: FinancialCovenantType;
	covenantName: string;
	covenantTarget?: number;
	covenantTargetType: CovenantTargetTypes;
	covenantTargetDisplay: string;
	isTargetDefined: boolean;
}

export interface FinancialCovenantType {
	id: number;
	guid: string;
	covenantName: string;
}

export interface TermSheetFacilityCollateral {
	id: number;
	guid: string;
	collateralSecurityType: CollateralSecurityTypes;
	advancedRate?: number;
	availabilityLimitType: FacilityAvailabilityLimitTypes;
	otherAvailabilityLimit?: string;
}

export interface TermSheetFinancialReporting {
	id: number;
	guid: string;
	reportingQuality: CompanyFinancialStatementTypes;
	frequency: FacilityAmortizationPeriods;
	daysToDeliver: number;
}

export type CompanyFinancialStatementTypes = keyof typeof CompanyFinancialStatementTypesMap;
export const CompanyFinancialStatementTypesMap = {
	None: 'None',
	Audited: 'Audited',
	Reviewed: 'Reviewed',
	Compiled: 'Compiled',
	CompanyPrepared: 'Company Prepared',
	TaxReturns: 'Tax Returns',
};

export type FacilityAmortizationPeriods = keyof typeof FacilityAmortizationPeriodsMap;
export const FacilityAmortizationPeriodsMap = {
	Monthly: 'Monthly',
	Quarterly: 'Quarterly',
	SemiAnnually: 'Semi-Annually',
	Annually: 'Annually',
};

export interface TermSheetCondition {
	id: number;
	guid: string;
	conditionTypeId: number;
	type: string;
	conditionText: string;
}
